import React from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "../ui/DropdownMenu";
import InfoTooltip from "../ui/InfoTooltip";
import Tooltip from "../ui/Tooltip";
import {
  ChevronLeft,
  ChevronRight,
  Cloud,
  CloudCheck,
  CloudUpload,
  Download,
  FilePlus2,
  FolderArchive,
  FolderOpen,
  FolderUp,
  Keyboard,
  LogOut,
  Mail,
  MessageSquare,
  Info,
  Redo2,
  Save,
  Settings,
  Shield,
  SlidersHorizontal,
  Undo2,
  Upload,
} from "lucide-react";
import { useCanRedo, useCanUndo } from "../../stores/historyStore";
import {
  getPhysicsBlockedActionTitle,
  usePhysicsAuthoringLock,
  usePhysicsRuntimeAuthoringLock,
} from "../../utils/physicsGuards";
import { AuthMenuItem, AuthState } from "./AuthControls";
import { useIsMobilePanelViewport } from "./mainPanelHooks";
import {
  DEPLOYMENT_BUNDLE_HELP_TEXT,
  MAIN_MENU_DROPDOWN_ICON_CLASS,
  MAIN_MENU_DROPDOWN_TRIGGER_CLASS,
  MAIN_MENU_HOVER_SURFACE_CLASS,
  MJCF_EXPORT_DIFFERENCE_HELP_TEXT,
} from "./constants";

function MainMenuItemLabel({ Icon, children, className = "" }) {
  return (
    <span className={["flex min-w-0 items-center gap-2", className].filter(Boolean).join(" ")}>
      <Icon className={MAIN_MENU_DROPDOWN_ICON_CLASS} strokeWidth={2.25} aria-hidden="true" />
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}

function MainMenuItemLabelWithInfo({ Icon, label, tooltip }) {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <Icon className={MAIN_MENU_DROPDOWN_ICON_CLASS} strokeWidth={2.25} aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className="ml-4 inline-flex shrink-0">{tooltip}</span>
    </span>
  );
}

/** Hamburger glyph used for the main menu trigger (expanded header dropdown + mobile collapsed tab). */
function MainMenuHamburgerIcon({ className = "h-5 w-5" }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

/** File / export / undo menu: main panel header + desktop collapsed tab (mobile collapsed tab opens the panel only). */
const MainMenuDropdown = React.memo(function MainMenuDropdown({
  onNewProject,
  onUploadFilesClick,
  onUploadFolderClick,
  onExportXml,
  onExportEnvironmentZip,
  onQuickSaveEnvironment,
  onSaveCloudNamed,
  onOpenCloudProjects,
  onExportDeploymentBundle,
  onLoadEnvironmentClick,
  onUndo,
  onRedo,
  onOpenVisibilitySettings,
  onOpenShortcuts,
  onOpenGetUpdates,
  onOpenSendFeedback,
  onOpenAbout,
  onOpenPrivacySettings,
  exportProjectName,
  onExportProjectNameChange,
  onRefreshExportProjectName,
  quickProjectSavePending = false,
  cloudActionPending = false,
  cloudSaveMatchesCurrent = false,
  menuTriggerClassName,
  /** `start` helps left-edge triggers (collapsed main tab) open the panel to the right. */
  contentAlign = "end",
}) {
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();
  const authoringLocked = usePhysicsAuthoringLock();
  const physicsRuntimeLocked = usePhysicsRuntimeAuthoringLock();
  const historyLockTitle = getPhysicsBlockedActionTitle(authoringLocked, "use history");
  const projectEditLockTitle = getPhysicsBlockedActionTitle(authoringLocked, "edit the project");
  const newProjectLockTitle = getPhysicsBlockedActionTitle(
    physicsRuntimeLocked,
    "start a new project"
  );
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [saveMenuOpen, setSaveMenuOpen] = React.useState(false);
  const [saveCompleteTarget, setSaveCompleteTarget] = React.useState(null);
  const saveInputId = React.useId();

  const handleQuickSave = React.useCallback(async () => {
    const saved = await onQuickSaveEnvironment?.();
    if (saved) {
      setSaveCompleteTarget("download");
    }
  }, [onQuickSaveEnvironment]);

  const handleCloudSaveNamed = React.useCallback(async () => {
    const saved = await onSaveCloudNamed?.(exportProjectName);
    if (saved) {
      setSaveCompleteTarget("cloud");
    }
  }, [exportProjectName, onSaveCloudNamed]);

  const closeMenus = React.useCallback(() => {
    setSaveCompleteTarget(null);
    setSaveMenuOpen(false);
    setMenuOpen(false);
  }, []);

  React.useEffect(() => {
    if (!saveMenuOpen) {
      setSaveCompleteTarget(null);
      return;
    }
    if (!saveCompleteTarget) onRefreshExportProjectName?.();
  }, [onRefreshExportProjectName, saveCompleteTarget, saveMenuOpen]);

  const handleSaveCtaClick = React.useCallback((openPanel) => {
    setSaveCompleteTarget(null);
    setSaveMenuOpen(false);
    setMenuOpen(false);
    openPanel?.();
  }, []);

  const saveCompleteIsDownload = saveCompleteTarget === "download";

  const saveSuccessContent = (
    <div className="grid gap-2">
      <div className="flex items-center justify-center gap-2 px-1 py-1 text-center">
        {saveCompleteIsDownload ? (
          <Download className="h-5 w-5 shrink-0 text-emerald-400" aria-hidden />
        ) : (
          <CloudCheck className="h-5 w-5 shrink-0 text-emerald-400" aria-hidden />
        )}
        <div className="text-sm font-semibold text-white">
          {saveCompleteIsDownload ? "Download complete" : "Cloud save complete"}
        </div>
      </div>
      <button
        type="button"
        className="menu-button flex w-full items-center justify-center gap-2 px-2 py-1.5 text-sm"
        onClick={() => handleSaveCtaClick(onOpenSendFeedback)}
      >
        <MessageSquare className="h-4 w-4 shrink-0" aria-hidden />
        Send feedback
      </button>
      <button
        type="button"
        className="menu-button flex w-full items-center justify-center gap-2 px-2 py-1.5 text-sm"
        onClick={() => handleSaveCtaClick(onOpenGetUpdates)}
      >
        <Mail className="h-4 w-4 shrink-0" aria-hidden />
        Get updates
      </button>
    </div>
  );

  const saveIconButtonClass = "menu-button flex h-8 w-8 shrink-0 items-center justify-center p-0";

  const saveFormContent = (
    <AuthState>
      {({ isConfigured, isLoaded, isSignedIn, openSignIn }) => {
        const cloudSaveUnavailable = !isConfigured || !isLoaded || !isSignedIn;
        const cloudSaveDisabled = cloudActionPending || cloudSaveUnavailable;
        const cloudDisabledTooltip = cloudSaveUnavailable ? "Sign in to save to cloud" : null;

        return (
          <form
            className="grid gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (cloudSaveDisabled) return;
              void handleCloudSaveNamed();
            }}
          >
            <label htmlFor={saveInputId} className="text-xs font-medium text-gray-300">
              Project name
            </label>
            <input
              id={saveInputId}
              type="text"
              value={exportProjectName}
              onChange={(event) => {
                setSaveCompleteTarget(null);
                onExportProjectNameChange?.(event.target.value);
              }}
              className="w-full rounded-md border border-gray-600 bg-black/20 px-2 py-1.5 text-sm text-gray-100 placeholder:text-gray-500 focus:border-gray-500 focus:outline-none"
              placeholder="Untitled Scene"
              onKeyDown={(event) => event.stopPropagation()}
            />
            {!isSignedIn ? (
              <p className="px-1 text-center text-xs leading-snug text-gray-300">
                Download a local copy
                {isConfigured ? (
                  <>
                    , or{" "}
                    <button
                      type="button"
                      className="font-inherit text-xs font-medium text-gray-100 underline decoration-gray-500 underline-offset-2 hover:text-white focus:outline-none focus:ring-1 focus:ring-gray-500"
                      onClick={() => openSignIn?.()}
                    >
                      sign in
                    </button>{" "}
                    to save to the cloud.
                  </>
                ) : (
                  "."
                )}
              </p>
            ) : null}
            <div className="my-1 border-t border-gray-700/80" aria-hidden="true" />
            <div className="flex items-center gap-2">
              <Tooltip content={cloudDisabledTooltip} side="top" fullWidth>
                <button
                  type="submit"
                  className={`menu-button flex min-w-0 flex-1 items-center justify-center gap-2 px-2 py-1.5 text-sm ${
                    cloudActionPending ? "main-menu-cloud-save-pending" : ""
                  }`}
                  disabled={cloudSaveDisabled}
                >
                  <CloudUpload className="h-4 w-4 shrink-0" aria-hidden />
                  {cloudActionPending ? (
                    <span
                      className="h-3 w-3 shrink-0 rounded-full border-2 border-gray-200/90 border-t-transparent animate-spin"
                      aria-hidden="true"
                    />
                  ) : null}
                  <span className="truncate">
                    {cloudActionPending
                      ? "Saving..."
                      : cloudSaveMatchesCurrent
                        ? "Autosaved"
                        : "Save to Cloud"}
                  </span>
                </button>
              </Tooltip>
              <Tooltip content="Download project" side="top">
                <button
                  type="button"
                  className={saveIconButtonClass}
                  onClick={() => void handleQuickSave()}
                  disabled={quickProjectSavePending}
                  aria-label="Download project"
                >
                  <Download
                    className="h-4 w-4 shrink-0 text-gray-200"
                    strokeWidth={2}
                    aria-hidden
                  />
                </button>
              </Tooltip>
              <Tooltip content="Additional options" side="top">
                <button
                  type="button"
                  className={saveIconButtonClass}
                  onClick={() => {
                    setSaveCompleteTarget(null);
                    setSaveMenuOpen(false);
                    setMenuOpen(false);
                    onExportEnvironmentZip?.();
                  }}
                  aria-label="Additional save options"
                >
                  <Settings
                    className="h-4 w-4 shrink-0 text-gray-200"
                    strokeWidth={2}
                    aria-hidden
                  />
                </button>
              </Tooltip>
            </div>
          </form>
        );
      }}
    </AuthState>
  );

  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={menuTriggerClassName ?? MAIN_MENU_DROPDOWN_TRIGGER_CLASS}
          aria-label="Main menu"
        >
          <MainMenuHamburgerIcon />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={contentAlign}>
        <DropdownMenuItem
          onClick={onNewProject}
          disabled={physicsRuntimeLocked}
          title={newProjectLockTitle}
        >
          <MainMenuItemLabel Icon={FilePlus2}>New Project</MainMenuItemLabel>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onLoadEnvironmentClick}
          disabled={authoringLocked}
          title={projectEditLockTitle}
        >
          <MainMenuItemLabel Icon={FolderOpen}>Load Project</MainMenuItemLabel>
        </DropdownMenuItem>
        <AuthState>
          {({ isConfigured, isLoaded, isSignedIn }) => {
            const disabled =
              authoringLocked || !isConfigured || !isLoaded || !isSignedIn || cloudActionPending;
            if (!isConfigured || !isLoaded || !isSignedIn) return null;
            return (
              <DropdownMenuItem
                onClick={() => {
                  closeMenus();
                  onOpenCloudProjects?.();
                }}
                disabled={disabled}
                title={disabled ? projectEditLockTitle || "Sign in to save to cloud" : undefined}
              >
                <MainMenuItemLabel Icon={Cloud}>Cloud Projects</MainMenuItemLabel>
              </DropdownMenuItem>
            );
          }}
        </AuthState>
        <DropdownMenuSub open={saveMenuOpen} onOpenChange={setSaveMenuOpen}>
          <DropdownMenuSubTrigger className="justify-between">
            <MainMenuItemLabel Icon={Save}>Save Project</MainMenuItemLabel>
            <ChevronRight className="h-4 w-4 text-gray-400" aria-hidden="true" />
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent sideOffset={6} className="w-64 p-3">
            {saveCompleteTarget ? saveSuccessContent : saveFormContent}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            className="justify-between"
            disabled={authoringLocked}
            title={projectEditLockTitle}
          >
            <MainMenuItemLabel Icon={Upload}>Upload</MainMenuItemLabel>
            <ChevronRight className="h-4 w-4 text-gray-400" aria-hidden="true" />
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent sideOffset={6}>
            <DropdownMenuItem onClick={onUploadFilesClick}>
              <MainMenuItemLabel Icon={Upload}>Files</MainMenuItemLabel>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onUploadFolderClick}>
              <MainMenuItemLabel Icon={FolderUp}>Folder</MainMenuItemLabel>
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="justify-between">
            <MainMenuItemLabel Icon={Download}>Export</MainMenuItemLabel>
            <ChevronRight className="h-4 w-4 text-gray-400" aria-hidden="true" />
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent sideOffset={6}>
            <DropdownMenuItem onClick={onExportXml}>
              <MainMenuItemLabelWithInfo
                Icon={FolderArchive}
                label="Mujoco (MJCF)"
                tooltip={<InfoTooltip text={MJCF_EXPORT_DIFFERENCE_HELP_TEXT} widthClass="w-64" />}
              />
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExportDeploymentBundle}>
              <MainMenuItemLabelWithInfo
                Icon={FolderArchive}
                label="Deployment Bundle"
                tooltip={<InfoTooltip text={DEPLOYMENT_BUNDLE_HELP_TEXT} widthClass="w-64" />}
              />
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onUndo}
          disabled={!canUndo || authoringLocked}
          title={historyLockTitle}
        >
          <MainMenuItemLabel Icon={Undo2}>Undo</MainMenuItemLabel>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onRedo}
          disabled={!canRedo || authoringLocked}
          title={historyLockTitle}
        >
          <MainMenuItemLabel Icon={Redo2}>Redo</MainMenuItemLabel>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onOpenVisibilitySettings}>
          <MainMenuItemLabel Icon={SlidersHorizontal}>Visibility Settings</MainMenuItemLabel>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onOpenPrivacySettings}>
          <MainMenuItemLabel Icon={Shield}>Privacy Settings</MainMenuItemLabel>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onOpenShortcuts}>
          <MainMenuItemLabel Icon={Keyboard}>Shortcuts</MainMenuItemLabel>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onOpenGetUpdates}>
          <MainMenuItemLabel Icon={Mail}>Get Updates</MainMenuItemLabel>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onOpenSendFeedback}>
          <MainMenuItemLabel Icon={MessageSquare}>Send Feedback</MainMenuItemLabel>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onOpenAbout}>
          <MainMenuItemLabel Icon={Info}>About</MainMenuItemLabel>
        </DropdownMenuItem>
        <AuthMenuItem onCloseMenu={() => setMenuOpen(false)}>
          {({
            disabled,
            email,
            icon,
            isSignedIn,
            label,
            onManageAccount,
            onSelect,
            onSignOut,
            profileIcon: ProfileIcon,
          }) => (
            <>
              <DropdownMenuSeparator />
              {isSignedIn ? (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="justify-between">
                    <MainMenuItemLabel Icon={icon}>{label}</MainMenuItemLabel>
                    <ChevronRight className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent sideOffset={6} className="w-72 p-0">
                    <div className="flex min-w-0 items-center gap-3 px-4 py-3">
                      <ProfileIcon className="h-11 w-11 shrink-0 text-gray-100" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-gray-100">{label}</div>
                        {email ? (
                          <div className="truncate text-xs text-gray-400">{email}</div>
                        ) : null}
                      </div>
                    </div>
                    <DropdownMenuSeparator className="mx-0 my-0" />
                    <DropdownMenuItem disabled={disabled} onClick={onManageAccount}>
                      <MainMenuItemLabel Icon={Settings}>Manage Account</MainMenuItemLabel>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="mx-0 my-0" />
                    <DropdownMenuItem disabled={disabled} onClick={onSignOut}>
                      <MainMenuItemLabel Icon={LogOut}>Sign Out</MainMenuItemLabel>
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              ) : (
                <DropdownMenuItem disabled={disabled} onClick={onSelect}>
                  <MainMenuItemLabel Icon={icon}>{label}</MainMenuItemLabel>
                </DropdownMenuItem>
              )}
            </>
          )}
        </AuthMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

const MainPanelHeader = React.memo(function MainPanelHeader({
  onCollapse,
  onNewProject,
  onUploadFilesClick,
  onUploadFolderClick,
  onExportXml,
  onExportEnvironmentZip,
  onQuickSaveEnvironment,
  onSaveCloudNamed,
  onOpenCloudProjects,
  onExportDeploymentBundle,
  onLoadEnvironmentClick,
  onUndo,
  onRedo,
  onOpenVisibilitySettings,
  onOpenShortcuts,
  onOpenGetUpdates,
  onOpenSendFeedback,
  onOpenAbout,
  onOpenPrivacySettings,
  exportProjectName,
  onExportProjectNameChange,
  onRefreshExportProjectName,
  quickProjectSavePending,
  cloudActionPending,
  cloudSaveMatchesCurrent,
}) {
  const isMobilePanelViewport = useIsMobilePanelViewport();

  const menuDropdown = (
    <MainMenuDropdown
      onNewProject={onNewProject}
      onUploadFilesClick={onUploadFilesClick}
      onUploadFolderClick={onUploadFolderClick}
      onExportXml={onExportXml}
      onExportEnvironmentZip={onExportEnvironmentZip}
      onQuickSaveEnvironment={onQuickSaveEnvironment}
      onSaveCloudNamed={onSaveCloudNamed}
      onOpenCloudProjects={onOpenCloudProjects}
      onExportDeploymentBundle={onExportDeploymentBundle}
      onLoadEnvironmentClick={onLoadEnvironmentClick}
      onUndo={onUndo}
      onRedo={onRedo}
      onOpenVisibilitySettings={onOpenVisibilitySettings}
      onOpenShortcuts={onOpenShortcuts}
      onOpenGetUpdates={onOpenGetUpdates}
      onOpenSendFeedback={onOpenSendFeedback}
      onOpenAbout={onOpenAbout}
      onOpenPrivacySettings={onOpenPrivacySettings}
      exportProjectName={exportProjectName}
      onExportProjectNameChange={onExportProjectNameChange}
      onRefreshExportProjectName={onRefreshExportProjectName}
      quickProjectSavePending={quickProjectSavePending}
      cloudActionPending={cloudActionPending}
      cloudSaveMatchesCurrent={cloudSaveMatchesCurrent}
      contentAlign={isMobilePanelViewport ? "start" : "end"}
    />
  );

  const collapseControl = isMobilePanelViewport ? (
    <button
      type="button"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={onCollapse}
      className="draggable-panel-close-button shrink-0 cursor-pointer select-none rounded leading-none text-gray-400 outline-none focus:outline-none"
      aria-label="Hide panel"
    >
      <ChevronLeft className="h-5 w-5 flex-shrink-0" strokeWidth={2.5} aria-hidden />
    </button>
  ) : (
    <button
      type="button"
      onClick={onCollapse}
      className={`${MAIN_MENU_HOVER_SURFACE_CLASS} flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-gray-400 outline-none transition-colors duration-[400ms] ease-out hover:text-gray-200 focus:outline-none`}
      aria-label="Hide panel"
    >
      <ChevronLeft className="h-4 w-4" aria-hidden />
    </button>
  );

  const logoImg = (
    <img
      src="/icons/logo.svg"
      alt="Critter Logo"
      className="h-[1.75rem] w-auto shrink-0 select-none"
    />
  );

  const titleEl = (
    <h2
      aria-label="Critter Engine"
      className={`min-w-0 font-semibold text-gray-100 select-none ${
        isMobilePanelViewport ? "text-xs leading-tight text-center" : "grow truncate text-base"
      }`}
    >
      {isMobilePanelViewport ? (
        <>
          Critter
          <br />
          Engine
        </>
      ) : (
        "Critter Engine"
      )}
    </h2>
  );

  return (
    <div
      className={`flex items-center gap-2 border-b border-gray-600 ${
        isMobilePanelViewport ? "px-3 py-2" : "px-4 py-3"
      }`}
    >
      {isMobilePanelViewport ? (
        <>
          <div className="flex shrink-0 items-center">{menuDropdown}</div>
          <div className="flex min-w-0 flex-1 items-center justify-center gap-0.5">
            {logoImg}
            {titleEl}
          </div>
          {collapseControl}
        </>
      ) : (
        <>
          <div className="flex min-w-0 flex-1 items-center gap-0.5">
            {logoImg}
            {titleEl}
          </div>
          {collapseControl}
          {menuDropdown}
        </>
      )}
    </div>
  );
});

export { MainMenuDropdown, MainMenuHamburgerIcon };
export default MainPanelHeader;
